import { makeAutoObservable, runInAction } from 'mobx';
import {
  getCurrentReleaseNote,
  getAllVersions,
  getPublishedVersions,
  saveVersion,
  publishVersion,
  deleteVersion,
  dismissReleaseNote
} from '@/api/releaseNotes';
import overview from './Overview';

class ReleaseNotesStore {
  // 用户端状态
  currentVersion = null;
  popupVisible = false;

  // 管理端状态
  versions = [];
  publishedVersions = [];
  // 预览态数据：管理页 Preview → View All Updates 跳转 /changeLog/preview 时由弹窗写入
  previewVersion = null;
  currentEditingId = null;
  loading = false;
  publishedLoading = false;
  publishedLoadFailed = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  // 设置/清除预览态版本数据（仅存在于当前会话内存，刷新即丢失）
  setPreviewVersion(version) {
    this.previewVersion = version;
  }

  /**
   * 语义版本号比较（降序）
   * 支持 "3.1.0" vs "3.0.0"、"2.0" vs "1.9.1" 等
   */
  compareVersionsDesc(a, b) {
    const pa = (a.version || '').split('.').map(Number);
    const pb = (b.version || '').split('.').map(Number);
    const len = Math.max(pa.length, pb.length);
    for (let i = 0; i < len; i++) {
      const na = pa[i] || 0;
      const nb = pb[i] || 0;
      if (na !== nb) return nb - na; // 降序
    }
    return 0;
  }

  /**
   * 对版本列表排序（三级分组，组内降序）：
   * 1. 有 publishTime（已发布）→ 按 publishTime 降序
   * 2. 无 publishTime 但有 releaseDate（草稿）→ 按 releaseDate 降序
   * 3. 两者都没有 → 排最后，按 createTime 降序
   * 同组同日期时按语义版本号降序
   */
  sortVersions(versions) {
    const groupOf = (v) => {
      if (v.publishTime) return 0;
      if (v.releaseDate) return 1;
      return 2;
    };
    const dateKeyOf = (v) => {
      if (v.publishTime) return v.publishTime;
      if (v.releaseDate) return v.releaseDate;
      return v.createTime || v.create_time || 0;
    };
    return [...versions].sort((a, b) => {
      const groupA = groupOf(a);
      const groupB = groupOf(b);
      if (groupA !== groupB) return groupA - groupB;
      const dateA = new Date(dateKeyOf(a) || 0).getTime() || 0;
      const dateB = new Date(dateKeyOf(b) || 0).getTime() || 0;
      if (dateA !== dateB) return dateB - dateA;
      // 同日期按版本号降序
      return this.compareVersionsDesc(a, b);
    });
  }

  /**
   * 为版本列表补充历史遗留数据标记
   * 后端已打标则沿用；否则按数据特征判定：无 feature，
   * 或所有 feature 的 images 为空且 menuPath 为空（迁移/旧数据特征）
   */
  annotateLegacy(versions) {
    return (versions || []).map((v) => {
      if (v.legacy !== undefined) return v;
      const feats = v.features || [];
      const isLegacy =
        feats.length === 0 ||
        feats.every((f) => (f.images || []).length === 0 && !(f.menuPath || '').trim());
      return { ...v, legacy: isLegacy };
    });
  }

  // 获取当前生效的 Release Note
  // 版本号始终显示（用于顶部导航栏标签），弹框受 dismissed + 过期时间控制
  async fetchCurrentReleaseNote() {
    try {
      const res = await getCurrentReleaseNote();
      if (res?.code === 200) {
        const data = res.data;
        runInAction(() => {
          this.currentVersion = data;
          // 弹框显示条件：有数据 + 未关闭 + 未过期
          if (data) {
            overview.currentVersion = data.version;
            const notDismissed = !data.dismissed;
            // 过期基准：优先 publishTime；缺失时回退 releaseDate；两者都缺视为脏数据 fail-safe 不弹
            // popupExpiryDays = 0 → 仅发布当天（日历日）弹；> 0 → 发布后 N 天内弹
            const expiryBase = data.publishTime || data.releaseDate;
            let notExpired = true;
            if (expiryBase && data.popupExpiryDays != null) {
              if (data.popupExpiryDays === 0) {
                // 按日历日字符串比较（取 yyyy-MM-dd 前缀，避免依赖 Date 解析非 ISO 格式）
                const baseDay = String(expiryBase).slice(0, 10);
                const now = new Date();
                const nowDay = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                notExpired = baseDay === nowDay;
              } else {
                const expiry = new Date(expiryBase);
                expiry.setDate(expiry.getDate() + data.popupExpiryDays);
                notExpired = Date.now() <= expiry.getTime();
              }
            } else {
              // 走不到正常分支的都视为脏数据：基准日期缺失，或 popupExpiryDays 为 null
              // （DB 列无 NOT NULL 约束，直连 SQL 可写入 null）—— fail-safe 不弹
              notExpired = false;
            }
            this.popupVisible = notDismissed && notExpired;
          } else {
            this.popupVisible = false;
          }
        });
      }
    } catch (e) {
      console.error('Failed to fetch release note:', e);
    }
  }

  // 关闭弹窗
  async dismissPopup() {
    if (this.currentVersion?.version) {
      try {
        await dismissReleaseNote({ version: this.currentVersion.version });
      } catch (e) {
        console.error('Failed to dismiss:', e);
      }
      runInAction(() => {
        this.popupVisible = false;
      });
    }
  }

  // 获取已发布版本（changeLog 页面用，所有登录用户可访问）
  async fetchPublishedVersions() {
    this.publishedLoading = true;
    try {
      const res = await getPublishedVersions();
      if (res?.code === 200) {
        runInAction(() => {
          this.publishedVersions = this.sortVersions(this.annotateLegacy(res.data || []));
          this.publishedLoadFailed = false;
        });
      } else {
        runInAction(() => {
          this.publishedLoadFailed = true;
        });
      }
    } catch (e) {
      console.error('Failed to fetch published versions:', e);
      runInAction(() => {
        this.publishedLoadFailed = true;
      });
    } finally {
      runInAction(() => {
        this.publishedLoading = false;
      });
    }
  }

  // 获取所有版本（管理端用，需 ADMIN_CONSOLE 权限）
  async fetchAllVersions() {
    this.loading = true;
    try {
      const res = await getAllVersions();
      if (res?.code === 200) {
        runInAction(() => {
          // 历史遗留数据标记：后端已打标则直接使用；否则按数据特征判定（见 annotateLegacy）
          this.versions = this.sortVersions(this.annotateLegacy(res.data || []));
          // 默认选中第一个非遗留（legacy）版本，遗留历史数据仅在侧边栏隐藏、仍参与版本号去重
          const firstEditable = this.versions.find((v) => !v.legacy);
          if (firstEditable && !this.currentEditingId) {
            this.currentEditingId = firstEditable.id;
          }
        });
      }
    } catch (e) {
      console.error('Failed to fetch versions:', e);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  // 保存草稿
  async saveVersionData(data) {
    const res = await saveVersion(data);
    // 拦截器对业务错误码（201/900）只弹提示不 reject，必须在此主动抛错，
    // 否则页面会误以为保存成功（如软删除版本占用唯一索引导致 DB 查重失败）
    if (res?.code !== 200) {
      const err = new Error(res?.msg || 'Save failed');
      err.businessError = true;
      throw err;
    }
    // 固定选中刚保存的版本，避免刷新列表后选中漂移；
    // 新建时后端不返回 id（data.id 为空），fetchAllVersions 会因 currentEditingId 为空
    // 自动选中列表第一项（最新的已发布版本），导致随后发布命中错误版本
    // （"Only draft version can be published"或误弹"该版本已发布"）——因此刷新后按唯一版本号钉回
    const savedId = res.data?.id || data.id || null;
    await this.fetchAllVersions();
    const finalId = savedId
      || this.versions.find((v) => (v.version || '') === (data.version || ''))?.id
      || null;
    if (finalId) {
      runInAction(() => {
        this.currentEditingId = finalId;
      });
    }
    return finalId ? this.versions.find((v) => v.id === finalId) || null : null;
  }

  // 发布版本
  async publishVersionData(id) {
    const res = await publishVersion(id);
    if (res?.code !== 200) {
      const err = new Error(res?.msg || 'Publish failed');
      err.businessError = true;
      throw err;
    }
    await this.fetchAllVersions();
    // 新发布的版本成为最新生效版本，同步顶部导航栏版本号标签
    await this.refreshCurrentReleaseNote();
  }

  // 刷新当前生效的 Release Note（同步顶部导航栏版本号标签，不重新触发弹窗）
  async refreshCurrentReleaseNote() {
    try {
      const res = await getCurrentReleaseNote();
      if (res?.code === 200) {
        const data = res.data;
        runInAction(() => {
          this.currentVersion = data || null;
          if (data) {
            overview.currentVersion = data.version;
          } else {
            this.popupVisible = false;
          }
        });
      }
    } catch (e) {
      console.error('Failed to refresh current release note:', e);
    }
  }

  // 删除版本
  async deleteVersionData(id) {
    const deleted = this.versions.find((v) => v.id === id);
    const res = await deleteVersion(id);
    if (res?.code !== 200) {
      const err = new Error(res?.msg || 'Delete failed');
      err.businessError = true;
      throw err;
    }
    if (this.currentEditingId === id) {
      runInAction(() => {
        this.currentEditingId = null;
      });
    }
    await this.fetchAllVersions();
    // 删除的正是顶部导航栏标签当前展示的生效版本时，重新拉取以同步版本号
    if (
      deleted &&
      this.currentVersion &&
      (deleted.version || '') === (this.currentVersion.version || '')
    ) {
      await this.refreshCurrentReleaseNote();
    }
  }

  // 设置当前编辑版本
  setCurrentEditingId(id) {
    this.currentEditingId = id;
  }

  // 获取当前编辑的版本
  get currentEditingVersion() {
    return this.versions.find((v) => v.id === this.currentEditingId) || null;
  }

  // 当前编辑版本是否已发布
  get isCurrentPublished() {
    return this.currentEditingVersion?.status === 'PUBLISHED';
  }
}

const releaseNotesStore = new ReleaseNotesStore();
export default releaseNotesStore;
